import React from 'react';
import { useDispatch } from 'react-redux';
import styles from './PurposeInfo.styles';
import ListEditItem from '../../shared/ListEditItem';
import { useIntl } from 'react-intl';
import { Tag } from 'antd';
import { renderText } from '../../shared/DrawerLayout/renderText';
import {
  formatISODuration,
  formatOverrideDuration,
  getOverrideAudienceKeys,
  USERS_COLLECTION,
} from '../../../utils';
import { RetentionOverrideInput } from '../../../@types';
import { openConfirmModal, openModal } from '../../../redux/store/ui/common';
import { archivePurposes } from '../../../redux/store/appData/privacyPolicy';
import { GroupedPurposeView } from '../types';

type Props = {
  groupedPurpose: GroupedPurposeView;
};

const PurposeInfo = ({ groupedPurpose }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const onRemove = () => {
    dispatch(
      openConfirmModal({
        destructive: true,
        title: intl.formatMessage({
          id: `fleetadmin.purpose.archive.msg`,
        }),
        onConfirm: () => {
          // Archive all purposes in the group
          const purposeIds = groupedPurpose.purposes.map((p) => p.id);
          dispatch(archivePurposes(purposeIds));
        },
      })
    );
  };

  const onEdit =
    groupedPurpose?.status === 'PUBLISHED'
      ? undefined
      : () => {
          dispatch(openModal('PurposeStatusModal'));
        };

  const renderStartDate = (value) => (
    <Tag color="#e9d9c4" style={{ color: '#663403' }}>
      {intl.formatMessage({ id: `${value}` })}
    </Tag>
  );

  const renderRetentionOverrides = (overrides: RetentionOverrideInput[]) => {
    if (!overrides?.length) return renderText(null, true);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', rowGap: 6 }}>
        {overrides.map((override, index) => {
          const audienceKeys = getOverrideAudienceKeys(override);
          const isBlocked = override?.blocked === true;
          return (
            <div
              key={`${index}-${audienceKeys.join('|')}`}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 6,
                rowGap: 2,
              }}
            >
              <span style={{ display: 'inline-flex', flexWrap: 'wrap' }}>
                {audienceKeys.map((id) => (
                  <Tag
                    key={id}
                    style={{
                      color: isBlocked ? '#7d3c07' : '#666668',
                      backgroundColor: isBlocked ? '#fdebd0' : '#e7e7e7',
                      margin: 2,
                    }}
                    className="merchant-tag"
                  >
                    {intl.formatMessage({ id })}
                  </Tag>
                ))}
              </span>
              <span style={{ whiteSpace: 'nowrap' }}>
                {formatOverrideDuration(override)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCollections = (collectionNames: string[]) => (
    <div>
      {collectionNames?.map((collection) => (
        <Tag
          key={collection}
          style={{
            color: '#1a5276',
            backgroundColor: '#d4e6f1',
            margin: 2,
          }}
          className="merchant-tag"
        >
          {intl.formatMessage({ id: `privacy.collectionName.${collection}` })}
        </Tag>
      ))}
    </div>
  );

  return (
    <>
      <div className="policy-info">
        <ListEditItem
          label={intl.formatMessage({
            id: `fleetadmin.purpose.name`,
          })}
          leftItem={renderText(groupedPurpose.baseName, true)}
          onClickEdit={onEdit}
        />

        <ListEditItem
          label={intl.formatMessage({
            id: `fleetadmin.purpose.collectionName`,
          })}
          leftItem={renderCollections(groupedPurpose.collectionNames)}
          onClickEdit={onEdit}
        />

        <ListEditItem
          label={intl.formatMessage({
            id: `fleetadmin.purpose.startdate`,
          })}
          leftItem={renderStartDate(groupedPurpose?.activationEventKey)}
          onClickEdit={onEdit}
        />

        <ListEditItem
          label={intl.formatMessage({
            id: `fleetadmin.purpose.minduration`,
          })}
          leftItem={
            <span>{formatISODuration(groupedPurpose?.minDuration)}</span>
          }
          onClickEdit={onEdit}
        />

        <ListEditItem
          label={intl.formatMessage({
            id: `fleetadmin.purpose.maxduration`,
          })}
          leftItem={
            <span>{formatISODuration(groupedPurpose?.maxDuration)}</span>
          }
          onClickEdit={onEdit}
        />

        {groupedPurpose?.collectionNames?.includes(USERS_COLLECTION) && (
          <ListEditItem
            label={intl.formatMessage({
              id: 'fleetadmin.purpose.retentionoverrides',
            })}
            leftItem={renderRetentionOverrides(
              groupedPurpose?.retentionOverrides
            )}
            onClickEdit={onEdit}
          />
        )}

        <ListEditItem
          label={intl.formatMessage({
            id: `fleetadmin.purpose.groups`,
          })}
          leftItem={
            <div>
              {groupedPurpose?.fieldGroupKeys?.map((group) => (
                <Tag
                  key={group}
                  style={{
                    color: '#666668',
                    backgroundColor: '#e7e7e7',
                    margin: 2,
                  }}
                  className="merchant-tag"
                >
                  {intl.formatMessage({ id: `${group}` })}
                </Tag>
              ))}
            </div>
          }
          onClickEdit={onEdit}
        />

        <ListEditItem
          label={intl.formatMessage({
            id: `fleetadmin.purpose.legalbasis`,
          })}
          leftItem={renderText(groupedPurpose.legalBasis, true)}
          onClickEdit={onEdit}
        />

        <ListEditItem
          label={intl.formatMessage({
            id: `fleetadmin.policy.info.name`,
          })}
          leftItem={renderText(groupedPurpose?.policyName, true)}
        />

        {groupedPurpose?.status !== 'PUBLISHED' && (
          <>
            <div className="policy-section-title">
              {intl.formatMessage({
                id: `fleetadmin.users.status.actions`,
              })}
            </div>
            <div className="red-action" onClick={onRemove}>
              {intl.formatMessage({
                id: `fleetadmin.purpose.info.remove`,
              })}
            </div>
          </>
        )}
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default PurposeInfo;
